import { useEffect, useRef, useState } from "react";

/**
 * Turnstile — Cloudflare bot-protection widget.
 *
 * Renders nothing (and reports a null token) unless REACT_APP_TURNSTILE_SITE_KEY
 * is set, so local/dev and un-configured environments are unaffected. The
 * backend captcha check is likewise a no-op until TURNSTILE_SECRET is set, so
 * the two sides stay in lock-step.
 *
 * Props:
 *   onToken(token | "")  — called with the solved token, or "" when it expires.
 */
const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;
const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js";

let scriptPromise = null;
function loadScript() {
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = reject;
    document.head.appendChild(s);
  });
  return scriptPromise;
}

export default function Turnstile({ onToken }) {
  const ref = useRef(null);
  const widgetId = useRef(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!SITE_KEY) return undefined;
    let cancelled = false;
    loadScript()
      .then(() => {
        if (cancelled || !ref.current || !window.turnstile) return;
        widgetId.current = window.turnstile.render(ref.current, {
          sitekey: SITE_KEY,
          callback: (token) => onToken && onToken(token),
          "expired-callback": () => onToken && onToken(""),
          "error-callback": () => onToken && onToken(""),
        });
      })
      .catch(() => setFailed(true));
    return () => {
      cancelled = true;
      try {
        if (widgetId.current && window.turnstile) window.turnstile.remove(widgetId.current);
      } catch {
        /* ignore */
      }
    };
    // onToken is stable enough for this widget's lifetime; render once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!SITE_KEY) return null;
  if (failed) {
    return (
      <p
        className="font-mono text-[11px] uppercase tracking-[0.22em] text-pink"
        data-testid="turnstile-error"
      >
        Couldn't load bot protection. Refresh and try again.
      </p>
    );
  }
  return <div ref={ref} data-testid="turnstile-widget" className="min-h-[65px]" />;
}

/** True when Turnstile is configured (frontend). Lets pages require a token. */
export const TURNSTILE_ENABLED = Boolean(SITE_KEY);
